import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    ArrowLongRightIcon,
    CalendarDaysIcon,
    ChatBubbleBottomCenterTextIcon
} from '@heroicons/react/24/outline';
import { getTodayIso } from '@odo/shared-ui';
import { createAppointment } from '../../../services/appointment';
import { fetchTimeline } from '../../../services/timeline';
import { usePatientCard } from '../../../hooks/usePatientCard';
import { useToast } from '../../../providers/ToastProvider';
import { useStatusCatalog } from '../../../services/statusCatalog';
import { StatusChip } from '../StatusChip';
import { statusPreview, type StatusPreview } from '../statusPreview';
import { TaskSidebarPanel } from './TaskSidebarPanel';
import { buildSidebarTabs, OPS_TABS } from './taskSidebarTabs';
import { AppointmentDateField } from '../../shared/AppointmentDateField';
import { AppointmentTimeField } from './schedule-appointment/AppointmentTimeField';
import type { TaskBodyProps } from './index';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
// Mirrors the legacy schedule endpoint's DEFAULT_SCHEDULE_REASON — the create
// endpoint persists NULL when no reason is sent, so default it here instead.
const DEFAULT_SCHEDULE_REASON = 'ODO requested appointment';

function formatDate(iso: string | null): string | null {
    if (!iso) return null;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString();
}

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between items-center py-1.5 gap-2">
            <span className="text-[12px] font-medium text-gray-500 flex-shrink-0">{label}</span>
            <span className="text-[12.5px] text-gray-800 truncate min-w-0">{value}</span>
        </div>
    );
}

// Hold - 2 ("In-office step needed"): ODO schedules the date the practice shared in chat, which
// writes the next appointment and exits the hold back to the pre-hold status.
export function ScheduleAppointmentTask({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    const { showToast } = useToast();
    const catalog = useStatusCatalog();
    const { data: card } = usePatientCard(c.id);

    const todayIso = getTodayIso();
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // This task drives its own completion, so hide TaskSurface's action buttons.
    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: false, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The note ODO sent the practice when this hold was placed (why the
    // in-office appointment is needed). Same event the dashboard modal shows.
    const { data: holdNoteResponse, isLoading: isHoldNoteLoading } = useQuery({
        queryKey: ['patient-timeline', c.id, 'in-office-hold-note'],
        queryFn: () => fetchTimeline(c.id, { type: 'in-office-hold-note', limit: 1 }),
        staleTime: 60 * 1000
    });
    const rawHoldReason = holdNoteResponse?.data?.[0]?.payload?.['reason'];
    const holdNote =
        typeof rawHoldReason === 'string' && rawHoldReason.trim() !== '' ? rawHoldReason : null;

    const appointmentReason = card?.dates?.nextAppointmentReason ?? null;
    const onHoldSince = formatDate(c.stepEnteredAt);

    // From -> to transition preview, matching the Exit-hold modal presentation.
    const fromPreview: StatusPreview = {
        phase: statusMeta.phase,
        name: catalog.display(statusMeta.id)
    };
    const resume = c.resumeFromStatus;
    const toPreview: StatusPreview | undefined =
        resume == null ? undefined : statusPreview(resume, catalog);

    // The hold's single forward edge (external actor: the surface hides it, we own it).
    const exitHoldAction = statusMeta.outgoingActions.find((a) => a.action === 'exit-hold');

    const canSubmit = DATE_PATTERN.test(date) && !submitting;

    const handleSubmit = async () => {
        if (!DATE_PATTERN.test(date)) return;
        setSubmitting(true);
        try {
            const result = await createAppointment(c.id, {
                date,
                time: time || undefined,
                reason: reason.trim() || DEFAULT_SCHEDULE_REASON
            });
            // exitedHold false: the case left this hold elsewhere first; the appointment is
            // saved, so close without the "case advanced" completion card.
            onTaskComplete?.(result.exitedHold ? exitHoldAction : undefined);
        } catch (err) {
            showToast(
                err instanceof Error ? err.message : 'Failed to schedule appointment.',
                'error'
            );
            setSubmitting(false);
        }
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 flex gap-3 px-4 pt-3 pb-3">
                {/* LEFT: why the appointment was requested, then date + destination */}
                <div className="flex-[6] min-w-0 min-h-0 overflow-y-auto flex flex-col gap-3">
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                            Why this appointment
                        </p>
                        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
                            <div className="flex items-center gap-1.5 mb-1">
                                <ChatBubbleBottomCenterTextIcon className="w-3.5 h-3.5 text-amber-600" />
                                <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">
                                    ODO note to the practice
                                </span>
                            </div>
                            {isHoldNoteLoading ? (
                                <p className="text-xs text-amber-700 opacity-60">Loading note…</p>
                            ) : (
                                <p className="text-sm text-amber-900 whitespace-pre-wrap">
                                    {holdNote ?? 'No note was recorded when this hold was placed.'}
                                </p>
                            )}
                        </div>
                        <div className="mt-2 divide-y divide-gray-100">
                            {appointmentReason && (
                                <InfoRow label="Reason on file" value={appointmentReason} />
                            )}
                            {onHoldSince && <InfoRow label="On hold since" value={onHoldSince} />}
                        </div>
                    </div>

                    {/* flex-1: this is the section the practice actually acts on, so it
                        stretches to claim the rest of the column instead of sizing to
                        content - the date/time fields stack full-width (rather than the
                        old 2-up grid) so the extra height reads as room to breathe, not
                        dead space, and the resume preview anchors to the bottom via
                        mt-auto. */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4 flex-1 flex flex-col">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                            Schedule the appointment
                        </p>
                        <div className="flex flex-col gap-4">
                            <label className="block">
                                <span className="block text-xs font-semibold text-gray-700 mb-1">
                                    Date
                                </span>
                                <AppointmentDateField
                                    value={date}
                                    onChange={setDate}
                                    todayIso={todayIso}
                                    disabled={submitting}
                                />
                            </label>
                            <label className="block">
                                <span className="block text-xs font-semibold text-gray-700 mb-1">
                                    Time{' '}
                                    <span className="font-normal text-gray-400">(optional)</span>
                                </span>
                                <AppointmentTimeField
                                    value={time}
                                    onChange={setTime}
                                    disabled={submitting}
                                />
                            </label>
                            <label className="block">
                                <span className="block text-xs font-semibold text-gray-700 mb-1">
                                    Reason{' '}
                                    <span className="font-normal text-gray-400">(optional)</span>
                                </span>
                                <input
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Type a reason"
                                    disabled={submitting}
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-400 bg-white transition-colors disabled:opacity-50"
                                />
                            </label>
                        </div>

                        {/* Same from -> to preview as the Exit-hold modal */}
                        <div className="mt-auto pt-4">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3">
                                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                    On scheduling, the case resumes
                                </p>
                                <div className="grid grid-cols-[1fr_auto_1fr] gap-2.5 items-center">
                                    <StatusChip preview={fromPreview} />
                                    <ArrowLongRightIcon className="w-5 h-5 text-gray-400" />
                                    {toPreview ? (
                                        <StatusChip preview={toPreview} />
                                    ) : (
                                        <div className="text-[11px] text-gray-400 italic px-2.5 py-2">
                                            Prior step (resolved by the engine)
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT: full-height sidebar with Patient info, Patient notes, Patient files */}
                <div className="flex-[4] min-w-0 min-h-0">
                    <TaskSidebarPanel tabs={buildSidebarTabs(c.id, OPS_TABS)} />
                </div>
            </div>

            {/* Footer: this body owns submit (the surface hides external-actor edges) */}
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-200 flex-shrink-0">
                <span className="text-xs text-gray-400 italic">
                    Performs the practice's "Schedule appointment" action on their behalf.
                </span>
                <button
                    type="button"
                    onClick={() => {
                        void handleSubmit();
                    }}
                    disabled={!canSubmit}
                    className="flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <CalendarDaysIcon className="w-4 h-4" />
                    {submitting ? 'Scheduling…' : 'Schedule appointment'}
                </button>
            </div>
        </div>
    );
}
